import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import urlUtils from 'utils/Url';
import localeUtils from 'utils/LanguageLocale';

/**
 * Gating helpers for the Customer Service SMS "Text Us" tile (ECSC-9743, ECSC-9919).
 *
 * The locale's `customerServiceSMS` feature flag is the master gate. Only when
 * that flag is enabled is the carrier-verification preview honored:
 *   show = flagEnabled(locale) AND (preview param present OR preview marker set)
 *
 * In other words:
 *   - Flag OFF  -> tile is never shown, even with the param or a stored marker.
 *   - Flag ON   -> tile is shown only when the page was visited with
 *                  `?sms_preview=carrier_verification`, or a marker from a prior
 *                  preview visit exists (so a carrier reviewer can navigate away
 *                  and return without the param). Flag ON alone does not show it.
 *
 * Visibility is ALWAYS resolved client-side only so the tile never lands in the
 * server-rendered (SSR/SEO) HTML. The preview param lets SMS carriers verify the
 * publicly visible phone number on production. Once A2P approval is granted, the
 * preview param + marker logic can be removed.
 */

const SMS_PREVIEW_PARAM = 'sms_preview';
const SMS_PREVIEW_VALUE = 'carrier_verification';

/**
 * Reads the locale-specific `customerServiceSMS` feature flag.
 * @returns {boolean} true when the flag for the current locale is enabled.
 */
const isSmsTileFlagEnabled = () => {
    const config = Sephora.configurationSettings?.customerServiceSMS;

    if (localeUtils.isFRCanada()) {
        return !!config?.caFrench?.isEnabled;
    }

    if (localeUtils.isCanada()) {
        return !!config?.caEnglish?.isEnabled;
    }

    return !!config?.usEnglish?.isEnabled;
};

/**
 * @returns {boolean} true when the carrier-verification preview param is present.
 */
const hasSmsPreviewParam = () => urlUtils.getParamValueAsSingleString(SMS_PREVIEW_PARAM) === SMS_PREVIEW_VALUE;

/**
 * @returns {boolean} true when a prior preview visit left a local storage marker.
 */
const hasSmsPreviewMarker = () => Storage.local.getItem(LOCAL_STORAGE.CUSTOMER_SERVICE_SMS_PREVIEW) === true;

/**
 * Persists the preview marker so the tile stays visible on later visits without
 * the param.
 */
const setSmsPreviewMarker = () => Storage.local.setItem(LOCAL_STORAGE.CUSTOMER_SERVICE_SMS_PREVIEW, true);

/**
 * Resolves whether the SMS "Text Us" tile should render. MUST be called
 * client-side only (e.g. inside useEffect); returns false during SSR so the
 * tile is never part of the server-rendered HTML.
 *
 * The locale feature flag is the master gate: the preview param / marker are
 * only honored when the flag is enabled.
 *
 * Side effect: persists the local storage marker on a param visit (flag on).
 * @returns {boolean}
 */
const shouldRenderSmsTile = () => {
    if (Sephora.isNodeRender) {
        return false;
    }

    if (!isSmsTileFlagEnabled()) {
        return false;
    }

    if (hasSmsPreviewParam()) {
        setSmsPreviewMarker();

        return true;
    }

    return hasSmsPreviewMarker();
};

export {
    SMS_PREVIEW_PARAM,
    SMS_PREVIEW_VALUE,
    isSmsTileFlagEnabled,
    hasSmsPreviewParam,
    hasSmsPreviewMarker,
    setSmsPreviewMarker,
    shouldRenderSmsTile
};

export default shouldRenderSmsTile;
